import React from 'react';
import { Grid, Button, Typography, Tooltip, Box } from '@mui/material';
import { IFillCoordinationTask } from 'interfaces/redux/task-types/IFillCoordinationTask';
import { ITherapy } from 'interfaces/redux/ITherapy';
import { formatTherapyNameLong } from 'services/utils/therapy-service';
import { THERAPY_MED_SYNC, SL_INCLUSION_STATUS } from 'constants/index';
import { buildQaId } from 'utils/build-qa-id';
import { MedSync as MedSyncIcon } from 'containers/patient/med-sync/components/medSyncIcon';
import {
  CAIcon,
  DSIcon,
  PRNIcon,
  APIcon,
  getCMIcon,
  MedicarePartBIcon,
  TrashCanIcon,
  FastForwardIcon,
  PillIcon,
} from 'components/icons/icons';
import TherapyOverrideModal from './therapy-override-modal';

type Props = {
  task: IFillCoordinationTask;
  therapy?: ITherapy;
  formValues: any;
  classes: any;
  disable_fc_removal: boolean;
  fieldSettings: any;
  fcInclusionStatus: any;
  isAnOrderTask: any;
  setFcInclusionStatus: any;
};

type FcInclusionStatus = {
  status: number;
  new_nbd: string | null;
  therapy_id: number | null;
  ds: number | null;
  fill_cycle_number: number | undefined;
};

const qaIdBuilder = buildQaId('dosing-regimen', '.');

function renderPillIcon(therapy: ITherapy) {
  const { dosis_regimen } = therapy;
  return dosis_regimen ? (
    <div style={{ display: 'flex' }} data-qa-id={qaIdBuilder('grid')}>
      <PillIcon therapy={therapy} />
      <Typography style={{ fontSize: 12, marginLeft: 10 }}>{dosis_regimen}</Typography>
    </div>
  ) : null;
}

export function RenderSimpleTherapyFields(props: Props) {
  const {
    task,
    therapy,
    formValues,
    classes,
    fieldSettings,
    fcInclusionStatus,
    isAnOrderTask,
    setFcInclusionStatus,
    disable_fc_removal,
  } = props;
  const [overrideModalOpen, setOverrideModalOpen] = React.useState<boolean>(false);

  const formatNbdDisplay = (value?: string | null) => {
    if (!value) {
      return '';
    }

    const [year, month, day] = value.split('-');
    if (!year || !month || !day) {
      return value;
    }

    return `${month}/${day}/${year}`;
  };

  if (!therapy || !task) {
    return null;
  }
  const openRemoveTherapyDialog = (task_id: number) => {
    setFcInclusionStatus?.((prev: Record<number, FcInclusionStatus>) => ({
      ...prev,
      [task_id]: {
        ...prev[task_id],
        status: SL_INCLUSION_STATUS.MOVED_2,
        new_nbd: formValues.next_needsby_date?.[task_id]?.format('YYYY-MM-DD') || '',
        therapy_id: therapy.id,
        ds: therapy.days_supply || null,
        fill_cycle_number: task.fill_cycle_number,
      },
    }));
    setOverrideModalOpen(true);
  };

  const skipTherapy = (task_id: number) => {
    setFcInclusionStatus?.((prev: Record<number, FcInclusionStatus>) => ({
      ...prev,
      [task_id]: {
        ...prev[task_id],
        status: SL_INCLUSION_STATUS.SKIPPED_3,
        new_nbd: formValues.next_needsby_date?.[task_id]?.format('YYYY-MM-DD') || '',
        therapy_id: therapy.id,
        ds: therapy.days_supply || null,
        fill_cycle_number: task.fill_cycle_number,
      },
    }));
  };
  const reAddTherapy = (task_id: number) => {
    setFcInclusionStatus?.((prev: Record<number, FcInclusionStatus>) => ({
      ...prev,
      [task_id]: {
        ...prev[task_id],
        status: SL_INCLUSION_STATUS.INCLUDED_1,
        new_nbd: null,
        therapy_id: therapy.id,
        ds: null,
        fill_cycle_number: task.fill_cycle_number,
      },
    }));
    setOverrideModalOpen(false);
  };
  const therapyDrugFullName = formatTherapyNameLong(
    therapy.drug_name,
    therapy.dosage_form,
    therapy.strength,
    therapy.strength_unit_of_measure,
    therapy.dosage_form_description,
    therapy.ndc,
  );
  const therapyForm = therapy.dosage_form_description || therapy.dosage_form || '';
  const therapyStrength = therapy.strength || '';
  const therapyUnits = therapy.strength_unit_of_measure || '';
  const therapyTitleParts = [therapy.drug_name, therapyForm, therapyStrength, therapyUnits].filter(
    value => Boolean(value),
  );
  const removeTherapyTitle = `Remove ${therapyTitleParts.join(' ')} from this order`;
  const inMedSync = therapy.in_med_sync === THERAPY_MED_SYNC.IN_MED_SYNC;
  const therapy_inclusion_status =
    fcInclusionStatus?.[task.id]?.status ?? SL_INCLUSION_STATUS.INCLUDED_1;
  return (
    <React.Fragment key={task.id}>
      <Grid container spacing={2} className={classes.therapyFormWrapper}>
        {fieldSettings.drug_name && (
          <Grid item xs={12}>
            <div className={classes.secureLinkTherapyList} data-fc-id={task.id}>
              {therapy.adherence_packaging ? <APIcon className={classes.prnIcon} /> : ''}
              <span style={{ whiteSpace: 'nowrap' }}>
                {therapyDrugFullName} | FC#{task.fill_cycle_number}
              </span>
              {therapy.as_needed ? <PRNIcon className={classes.prnIcon} /> : ''}
              {therapy.clinic_administered ? <CAIcon className={classes.prnIcon} /> : ''}
              {therapy.medicare_part_b ? (
                <Tooltip title="Medicare Part B">
                  <MedicarePartBIcon />
                </Tooltip>
              ) : null}
              {therapy?.drop_ship_med ? (
                <Tooltip title="Drop-Ship Med">
                  <span>
                    <DSIcon className={classes.prnIcon} />
                  </span>
                </Tooltip>
              ) : null}
              {therapy.controlled_substance_code
                ? getCMIcon(therapy?.controlled_substance_code, classes.prnIcon)
                : ''}
              {therapy.one_time_fill ? (
                <Tooltip title="One-Time Fill">
                  <Typography component="span" className={classes.otfIcon}>
                    OTF
                  </Typography>
                </Tooltip>
              ) : (
                ''
              )}
              {inMedSync ? (
                <Box sx={{ opacity: 0.58 }}>
                  <MedSyncIcon size="sm" />
                </Box>
              ) : null}
              {renderPillIcon(therapy)}
              {isAnOrderTask &&
              therapy_inclusion_status === SL_INCLUSION_STATUS.INCLUDED_1 &&
              !disable_fc_removal ? (
                <Box className={classes.therapyHeaderActions}>
                  <Tooltip title="Remove Therapy from this Order">
                    <Button onClick={() => openRemoveTherapyDialog(task.id)}>
                      <TrashCanIcon />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Skip Therapy for this Order">
                    <Button onClick={() => skipTherapy(task.id)}>
                      <FastForwardIcon />
                    </Button>
                  </Tooltip>
                </Box>
              ) : null}
              {isAnOrderTask && therapy_inclusion_status === SL_INCLUSION_STATUS.SKIPPED_3
                ? (() => {
                    return (
                      <Box className={classes.therapyHeaderActions}>
                        <Typography>
                          New NBD {formatNbdDisplay(fcInclusionStatus?.[task.id]?.new_nbd)} |{' '}
                        </Typography>
                        <Button
                          sx={{
                            zIndex: 10001,
                          }}
                          onClick={() => reAddTherapy(task.id)}
                        >
                          Add it back
                        </Button>
                      </Box>
                    );
                  })()
                : null}
              {isAnOrderTask &&
              therapy_inclusion_status === SL_INCLUSION_STATUS.MOVED_2 &&
              !overrideModalOpen ? (
                <Box className={classes.therapyHeaderActions}>
                  <Typography>
                    This therapy was removed from this order | New NBD{' '}
                    {formatNbdDisplay(fcInclusionStatus?.[task.id]?.new_nbd)} | Days Supply{' '}
                    {`${fcInclusionStatus?.[task.id]?.ds} | `}
                  </Typography>
                  <Button onClick={() => reAddTherapy(task.id)}>Add it back</Button>
                </Box>
              ) : null}
              {isAnOrderTask &&
              therapy_inclusion_status === SL_INCLUSION_STATUS.MOVED_2 &&
              overrideModalOpen ? (
                <TherapyOverrideModal
                  open={overrideModalOpen}
                  onCancel={() => reAddTherapy(task.id)}
                  onClose={() => setOverrideModalOpen(false)}
                  title={removeTherapyTitle}
                  fcInclusionStatus={fcInclusionStatus}
                  setFcInclusionStatus={setFcInclusionStatus}
                  task_id={task.id}
                />
              ) : null}
            </div>
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  );
}
